import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { publicApi } from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";
import Seo from "@/components/Seo";

const nfEUR = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, localized } = useLanguage();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    publicApi
      .getProperty(id)
      .then((data) => {
        setP(data);
        // If URL is the UUID form but property has a slug → replace URL (client-side 301-equivalent)
        if (data?.slug && UUID_RE.test(id) && data.slug !== id) {
          navigate(`/properties/${data.slug}`, { replace: true });
        }
      })
      .catch(() => setErr(true));
  }, [id, navigate]);

  if (err) {
    return (
      <div className="pt-40 pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="text-muted">Property not found.</p>
        <Link
          to="/properties"
          className="mt-6 inline-block underline underline-offset-8 text-sm"
        >
          {t("featured.view_all")}
        </Link>
      </div>
    );
  }
  if (!p) {
    return (
      <div className="pt-40 pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-1/3 bg-line" />
          <div className="h-[70vh] bg-line" />
        </div>
      </div>
    );
  }

  const title = localized(p.title);
  const description = localized(p.description);
  const seoTitle = localized(p.seo_title) || `${title} — JK Invest Group`;
  const seoDesc = localized(p.seo_description) || description?.slice(0, 155);
  const heroAlt = (p.image_alts && p.image_alts[0]) || title;

  return (
    <article data-testid="property-detail" className="pt-28 pb-24">
      <Seo
        title={seoTitle}
        description={seoDesc}
        keywords={p.seo_keywords || ""}
        image={p.images?.[0]}
        url={p.slug ? `${window.location.origin}/properties/${p.slug}` : undefined}
        type="article"
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
          <div className="md:col-span-8">
            <div className="overline mb-3">
              {p.location} · {p.type === "offmarket" ? "Off-Market" : p.type}
            </div>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-ink font-light">
              {title}
            </h1>
          </div>
          <div className="md:col-span-4 md:text-right">
            <div className="font-mono text-2xl text-ink">
              {p.off_market ? t("property_detail.price_on_request") : nfEUR.format(p.price)}
            </div>
            <div className="mt-2 text-sm text-muted">
              {t("properties.ref")} {p.reference}
            </div>
          </div>
        </div>

        {p.images?.[0] && (
          <div className="relative w-full h-[70vh] overflow-hidden">
            <img
              src={p.images[0]}
              alt={heroAlt}
              className={`w-full h-full object-cover transition-[filter,opacity] duration-500 ${
                p.status === "sold" ? "grayscale-[35%] opacity-90" : ""
              }`}
            />
            {(p.status === "sold" || p.status === "reserved") && (
              <div
                className={`absolute top-6 right-6 flex items-center gap-2 px-4 py-1.5 backdrop-blur-sm border text-xs tracking-widest2 uppercase ${
                  p.status === "sold"
                    ? "bg-black/55 border-white/25 text-white"
                    : "bg-white/75 border-ink/15 text-ink"
                }`}
                data-testid={`property-detail-status-${p.status}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${p.status === "sold" ? "bg-white/80" : "bg-ink/70"}`} />
                {p.status === "sold" ? t("properties.status_sold") : t("properties.status_reserved")}
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-12 gap-12 mt-16">
          <div className="md:col-span-7">
            <p className="text-lg leading-relaxed text-ink font-normal">
              {description}
            </p>

            {p.features?.length > 0 && (
              <div className="mt-12">
                <div className="overline mb-4">{t("property_detail.features")}</div>
                <ul className="grid grid-cols-2 gap-y-2 text-sm text-ink">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="text-accent mt-0.5">—</span> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Amenities grid */}
            {(() => {
              const AKS = ["exclusive","pool","parking","elevator","sea_view","beach_access","garden","terrace","ac","heating","security","gym","spa","sauna","wine_cellar","home_cinema","smart_home","solar_panels","staff_quarters"];
              const on = AKS.filter((k) => p[k]);
              if (!on.length) return null;
              return (
                <div className="mt-12">
                  <div className="overline mb-4">{t("properties.filter_amenity")}</div>
                  <ul className="grid grid-cols-2 md:grid-cols-3 gap-y-2 text-sm text-ink">
                    {on.map((k) => (
                      <li key={k} className="flex items-start gap-2">
                        <span className="text-accent mt-0.5">✓</span> {t(`amenities.${k}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {p.year_built ? (
              <div className="mt-8 text-sm text-muted">
                {t("amenities.year_built")}: <span className="font-mono text-ink">{p.year_built}</span>
              </div>
            ) : null}

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-line pt-8">
              <Stat label={t("properties.bedrooms")} value={p.bedrooms} />
              <Stat label={t("properties.bathrooms")} value={p.bathrooms} />
              <Stat label={t("properties.area")} value={p.area_m2} />
              {p.plot_m2 > 0 && (
                <Stat label={t("properties.plot")} value={p.plot_m2} />
              )}
            </div>

            {p.videos && p.videos.length > 0 && (
              <div className="mt-12">
                <div className="overline mb-4">{t("amenities.videos")}</div>
                <div className="grid gap-4">
                  {p.videos.map((v, i) => {
                    const yt = v.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
                    if (yt) {
                      return (
                        <div key={i} className="aspect-video w-full">
                          <iframe
                            src={`https://www.youtube.com/embed/${yt[1]}`}
                            title={`video-${i}`}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      );
                    }
                    return (
                      <video key={i} src={v} controls className="w-full aspect-video bg-black" />
                    );
                  })}
                </div>
              </div>
            )}

            {p.images && p.images.length > 1 && (
              <div className="mt-12 grid grid-cols-2 gap-4">
                {p.images.slice(1).map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={(p.image_alts && p.image_alts[i + 1]) || title}
                    className="w-full aspect-[4/3] object-cover"
                    loading="lazy"
                  />
                ))}
              </div>
            )}
          </div>

          <aside className="md:col-span-5 md:sticky md:top-28 md:self-start">
            <div className="bg-white border border-line p-8">
              <div className="overline mb-2">
                {t("properties.inquire")}
              </div>
              <h3 className="font-serif text-2xl mb-6">JK Invest Group</h3>
              <InquiryForm
                propertyId={p.id}
                compact
                testIdPrefix="property-inquiry"
                ctaLabel={t("properties.request_info")}
              />
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-2xl text-ink">{value}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}
